import React from 'react';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { GetAccountInfoQuery, PassengerFragment } from '@websky/graphql';
import { Form } from 'react-final-form';
import TravellerFields from './TravellerFields/TravellerFields';
import Button from '../../../BaseComponents/Button';
import Link from '../../../Link';
import ModalConfirm from '../../../ModalConfirm';
import { TravellerCardValues } from '../../types';
import { useRenderers } from '../../../renderProps';

interface Props {
	error?: string;
	isLoading?: boolean;
	hasLoyaltyProgramURL?: boolean;
	isOpen?: boolean;
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	travellers: PassengerFragment[];
	onClearError: React.DispatchWithoutAction;
	onOpenConnectModal: React.DispatchWithoutAction;
	onGoToAccountLoyalty: React.DispatchWithoutAction;
	onClose: React.DispatchWithoutAction;
	onSubmit: React.Dispatch<TravellerCardValues>;
}

const LinkCardsModal: React.FC<Props> = ({
	travellers,
	userInfo,
	onOpenConnectModal,
	onGoToAccountLoyalty,
	isLoading,
	onSubmit,
	error,
	onClearError,
	isOpen,
	onClose,
	hasLoyaltyProgramURL
}) => {
	const FORM_NAME = 'UPDATE_FFP_FORM';
	const theme = useTheme('Loyalty').LinkCardsModal;
	const { t } = useTranslation('LinkCardsModal');

	const { TravellerFields: TravellerFieldsOverride = TravellerFields } = useRenderers();

	const formRef = React.useRef(null);
	const defaultValues = React.useRef<TravellerCardValues>(null);

	const isLinkedFFP = Boolean(userInfo) && !userInfo.isFfpAuthorizationNeeded;
	const canCreateFFP = !isLinkedFFP;

	const onCloseModal = () => {
		onClose();
		formRef.current.reset(defaultValues.current);
	};

	const validate = (values: TravellerCardValues) => {
		if (values.travellers && !values.travellers.some(traveller => traveller.loyaltyCardNumber)) {
			return {
				travellers: values.travellers.map(_ => ({
					loyaltyCardNumber: t('At least one field must be filled in')
				}))
			};
		}

		return undefined;
	};

	return (
		<ModalConfirm
			isOpen={isOpen}
			onClose={onCloseModal}
			size="xlarge"
			title={
				<div className={theme.header}>
					<h2 className={theme.header__title}>{t('Loyalty cards')}</h2>
					<h3 className={theme.header__desc}>{t('Add participant loyalty program numbers')}</h3>
				</div>
			}
			footer={
				<div className={theme.footer}>
					<div className={theme.footer__info}>
						{canCreateFFP ? (
							<>
								<p>
									{t('or you can')}{' '}
									<Link action={onOpenConnectModal}>{t('Register in our Loyalty Program')}</Link>
								</p>
								<p>{t('Don’t miss out! Get your card now and unlock rewards!')}</p>
							</>
						) : (
							hasLoyaltyProgramURL && (
								<Link action={onGoToAccountLoyalty} target="_blank">
									{t('Want to check your miles history?')}
								</Link>
							)
						)}
					</div>
					<Button variant="smallPrimary" type="submit" isLoading={isLoading} form={FORM_NAME}>
						{t('Confirm')}
					</Button>
				</div>
			}
		>
			<div className={theme.form_container}>
				<Form onSubmit={onSubmit} validate={validate}>
					{({ handleSubmit, form, values }) => {
						formRef.current = form;

						// hack for correct reset form values
						if (!defaultValues.current && values.travellers) {
							defaultValues.current = values;
						}

						return (
							<form onSubmit={handleSubmit} id={FORM_NAME}>
								{travellers.map(traveller => (
									<TravellerFieldsOverride key={traveller.id} traveller={traveller} />
								))}
							</form>
						);
					}}
				</Form>
			</div>

			<ModalConfirm isOpen={!!error} title={t('Oops...')} onClose={onClearError} onConfirm={onClearError}>
				{error}
			</ModalConfirm>
		</ModalConfirm>
	);
};

export default LinkCardsModal;
